import React from 'react';
import { Link } from 'gatsby';
import styled, { keyframes } from 'styled-components';

import { H1 } from '@sayido/ui';

import { buttonStyles } from './Button';

const graphicAnimation = keyframes`
  50% {
    transform: rotateX(10deg) rotateY(-10deg) rotateZ(0deg);
  }
`;

const graphicAnimationAlt = keyframes`
  50% {
    transform: rotateX(-10deg) rotateY(0deg) rotateZ(0deg);
  }
`;

export const Title = H1;

export const SectionWrapper = styled.div`
  background-image: url(${p => p.styledBg || ''});
  background-size: cover;
  background-repeat: no-repeat;
  background-color: ${p =>
    p.bgColor || (p.layout === 'alt' ? 'var(--nude-200)' : '#fff')};
  padding: 160px 40px;
  text-align: ${p => p.align || 'left'};
  max-width: 1300px;
  width: calc(100% - 40px);

  iframe {
    max-width: 100%;
  }

  @media (max-width: 800px) {
    padding: 100px 10px;
    margin: 0 auto;
    width: calc(100% - 32px); // 16px margin on each side
  }

  ${Title} {
    text-align: ${p => p.align || 'left'};
  }
`;

const Content = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  max-width: 500px;
  justify-content: center;

  @media (max-width: 800px) {
    margin-top: 40px;
  }
`;

export const Body = styled.section`
  font-size: 1.2em;
  font-weight: normal;
  line-height: 1.4;
  margin: 0 0 2em 0;
  max-width: 400px;
`;

const ButtonInternal = styled(Link)`
  ${buttonStyles['default']};
  margin-top: 25px;
`;

const ButtonExternal = styled.a`
  ${buttonStyles['default']};
  margin-top: 25px;
`;

const Inner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: ${p => (p.columnTitle ? 'column' : 'row')};
  justify-content: space-evenly;
  margin: 0 auto;
  max-width: ${p => (p.fullWidth ? '100%' : '1260px')};
  width: ${p => (p.fullWidth ? '100%' : '97%')};

  @media (max-width: 800px) {
    align-items: center;
    flex-direction: column;
  }
`;

const Graphic = styled.div`
  min-height: 250px;
  margin: 0 4%;
  min-width: 300px;
  perspective: 1500px;

  > *:not(iframe) {
    animation: ${p =>
        p.animation
          ? p.layout === 'alt'
            ? graphicAnimationAlt
            : graphicAnimation
          : 'none'}
      10s ease-in-out infinite;
    border-radius: 5px;
    box-shadow: ${p =>
      p.shadow
        ? `0 19px 38px rgba(30,18,10, 0.15),
        0 15px 12px rgba(30,18,10, 0.08)`
        : 'none'};
    transform: ${p =>
      p.animation
        ? p.layout === 'alt'
          ? 'rotateX(-10deg) rotateY(20deg) rotateZ(3deg)'
          : 'rotateX(10deg) rotateY(-20deg) rotateZ(3deg)'
        : 'none'};
  }

  > img {
    width: 100%;
    display: block;
    margin: 0;
  }
  @media (max-width: 800px) {
    margin: 24px 12px;
  }
`;

const GraphicSectionInner = styled(Inner)`
  @media (min-width: 600px) {
    flex-direction: ${p => (p.layout === 'alt' ? 'row-reverse' : 'row')};
  }
`;

export const GraphicSection = ({
  animation = true,
  children,
  title,
  body,
  layout,
  ctaLabel,
  ctaLink,
  ctaTarget = '_parent',
  ctaInternal = false,
  shadow = true,
  styledBg = '',
  bgColor,
}) => {
  const Button = ctaInternal ? ButtonInternal : ButtonExternal;

  return (
    <SectionWrapper layout={layout} bgColor={bgColor} styledBg={styledBg}>
      <GraphicSectionInner layout={layout}>
        {children && (
          <Graphic animation={animation} layout={layout} shadow={shadow}>
            {children}
          </Graphic>
        )}
        <Content>
          <Title as={'h2'}>{title}</Title>
          <Body>
            {body}
            <p>
              {ctaLabel && ctaLink && (
                <Button href={ctaLink} to={ctaLink} target={ctaTarget}>
                  {ctaLabel}
                </Button>
              )}
            </p>
          </Body>
        </Content>
      </GraphicSectionInner>
    </SectionWrapper>
  );
};

const Subtitle = styled.p`
  margin: 0 auto;
  width: 100%;
  max-width: 700px;
  text-align: center;
`;

const Section = ({
  children,
  title = '',
  subtitle = '',
  fullWidth = false,
  layout = 'default',
  columnTitle = false,
}) => {
  return (
    <SectionWrapper layout={layout}>
      <Inner fullWidth={fullWidth} columnTitle={columnTitle}>
        {title && <Title as={'h2'}>{title}</Title>}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {children}
      </Inner>
    </SectionWrapper>
  );
};

export default Section;
